
import React, { useState, useRef } from 'react';
import { db } from '../services/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { UserProfile } from '../types';
import { put } from '@vercel/blob';

const BLOB_TOKEN = "vercel_blob_rw_zNodi0FoXXYvFHHJ_0ONOJAzweaSuTBnGC2ic3PMNNMUcAi";
const LOGO_URL = "https://i.ibb.co/NMthQdw/download-Photoroom.png";

const ProfileManagement: React.FC<{ user: UserProfile }> = ({ user }) => {
    const [name, setName] = useState(user.displayName);
    const [phone, setPhone] = useState(user.phoneNumber || '');
    const [isUpdating, setIsUpdating] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleUpdateProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsUpdating(true);
        try {
            const userRef = doc(db, 'users', user.uid);
            await updateDoc(userRef, {
                displayName: name,
                phoneNumber: phone
            });
            alert("Profile updated successfully!");
        } catch (err) {
            console.error(err);
            alert("Failed to update profile info.");
        } finally {
            setIsUpdating(false);
        }
    };

    const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUpdating(true);
        setUploadProgress(10);
        
        try {
            setUploadProgress(30);
            const blob = await put(`avatars/${user.uid}_${Date.now()}_${file.name}`, file, {
                access: 'public',
                token: BLOB_TOKEN,
            });
            
            setUploadProgress(80);
            const userRef = doc(db, 'users', user.uid);
            await updateDoc(userRef, { photoURL: blob.url });
            
            setUploadProgress(100);
            alert("Profile picture updated!");
            window.location.reload(); // Refresh to sync auth state/ui everywhere
        } catch (err) {
            console.error(err);
            alert("Photo upload failed.");
        } finally {
            setIsUpdating(false);
            setUploadProgress(0);
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8 animate-fadeIn">
            <header className="flex items-center justify-between">
                <div>
                    <h2 className="text-3xl font-black dark:text-white tracking-tight">Account Settings</h2>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">Personal Identity & Security</p>
                </div>
                <div className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest ${
                    user.role === 'admin' ? 'bg-amber-100 text-amber-600' : 
                    user.role === 'staff' ? 'bg-blue-100 text-blue-600' : 
                    'bg-purple-100 text-purple-600'
                }`}>
                    Level: {user.role}
                </div>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left: Avatar Section */}
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-white dark:bg-dark-card p-8 rounded-[3rem] border border-gray-100 dark:border-gray-800 shadow-xl flex flex-col items-center text-center">
                        <div className="relative group cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                            <div className="w-32 h-32 rounded-[2.5rem] overflow-hidden ring-4 ring-purple-500/10 shadow-2xl relative">
                                <img src={user.photoURL} className="w-full h-full object-cover transition-transform group-hover:scale-110" alt="Avatar" />
                                {isUpdating && uploadProgress > 0 && (
                                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                        <div className="w-12 h-12 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                )}
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                    <i className="fas fa-camera text-white text-xl"></i>
                                </div>
                            </div>
                            <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-purple-600 text-white rounded-2xl flex items-center justify-center shadow-lg border-2 border-white dark:border-dark-card">
                                <i className="fas fa-pencil text-xs"></i>
                            </div>
                            <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handlePhotoUpload} />
                        </div>

                        <div className="mt-6">
                            <h3 className="text-xl font-black dark:text-white leading-tight">{user.displayName}</h3>
                            <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mt-1">{user.email}</p>
                        </div>

                        <div className="w-full mt-8 pt-6 border-t border-gray-50 dark:border-gray-800 space-y-3">
                            <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest">
                                <span className="text-gray-400">UID Signature</span>
                                <span className="text-purple-600 font-mono">...{user.uid.slice(-6)}</span>
                            </div>
                            <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest">
                                <span className="text-gray-400">Storage Usage</span>
                                <span className="text-blue-500">Cloud Sync Active</span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-purple-600 p-8 rounded-[2.5rem] text-white shadow-2xl relative overflow-hidden group">
                        <i className="fas fa-shield-halved absolute -bottom-4 -right-4 text-8xl opacity-10 group-hover:scale-125 transition-transform"></i>
                        <h4 className="text-lg font-black mb-2">Secure Vault</h4>
                        <p className="text-xs text-purple-100 font-medium leading-relaxed">Your profile data is protected with end-to-end encryption and multi-factor authentication protocols.</p>
                    </div>
                </div>

                {/* Right: Info Form */}
                <div className="lg:col-span-2 space-y-6">
                    <form onSubmit={handleUpdateProfile} className="bg-white dark:bg-dark-card p-10 rounded-[3rem] border border-gray-100 dark:border-gray-800 shadow-xl space-y-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-gray-800 flex items-center justify-center text-purple-600">
                                <i className="fas fa-id-card"></i>
                            </div>
                            <h3 className="text-xl font-black dark:text-white">Identification</h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Legal Name</label>
                                <input 
                                    required 
                                    type="text" 
                                    value={name} 
                                    onChange={e => setName(e.target.value)}
                                    className="w-full bg-gray-50 dark:bg-gray-800 px-6 py-4 rounded-2xl dark:text-white font-bold outline-none focus:ring-2 focus:ring-purple-500/20 focus:bg-white transition-all border border-transparent"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Contact Phone</label>
                                <input 
                                    type="tel" 
                                    placeholder="+91 00000 00000"
                                    value={phone} 
                                    onChange={e => setPhone(e.target.value)}
                                    className="w-full bg-gray-50 dark:bg-gray-800 px-6 py-4 rounded-2xl dark:text-white font-bold outline-none focus:ring-2 focus:ring-purple-500/20 focus:bg-white transition-all border border-transparent"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Official Email (Read-Only)</label>
                            <div className="w-full bg-gray-50 dark:bg-gray-800/50 px-6 py-4 rounded-2xl text-gray-400 font-bold border border-transparent cursor-not-allowed flex justify-between items-center">
                                {user.email}
                                <i className="fas fa-lock text-[10px] opacity-30"></i>
                            </div>
                        </div>

                        <div className="pt-6 border-t border-gray-50 dark:border-gray-800 flex flex-col md:flex-row gap-4 items-center">
                            <button 
                                type="submit" 
                                disabled={isUpdating}
                                className="w-full md:w-auto px-10 py-5 bg-purple-600 text-white font-black rounded-2xl shadow-xl shadow-purple-200 dark:shadow-none hover:bg-purple-700 transition-all active:scale-95 flex items-center justify-center gap-3"
                            >
                                {isUpdating ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                                Commit Changes
                            </button>
                            <p className="text-[10px] text-gray-400 font-bold uppercase text-center md:text-left">Syncs across all BSD platforms immediately.</p>
                        </div>
                    </form>

                    <div className="bg-white dark:bg-dark-card p-10 rounded-[3rem] border border-gray-100 dark:border-gray-800 shadow-xl">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-gray-800 flex items-center justify-center text-blue-500">
                                <i className="fas fa-bell"></i>
                            </div>
                            <h3 className="text-xl font-black dark:text-white">Notifications</h3>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium leading-relaxed mb-6">Stay updated with the latest announcements and faculty broadcasts directly on your device.</p>
                        <button 
                            onClick={async () => {
                                if (!("Notification" in window)) {
                                    alert("This browser does not support desktop notification");
                                } else if (Notification.permission === "granted") {
                                    alert("Notifications are already enabled and linked to Firebase!");
                                } else {
                                    const permission = await Notification.requestPermission();
                                    if (permission === "granted") {
                                        const options = {
                                            body: "Notifications enabled successfully!",
                                            icon: LOGO_URL,
                                            badge: LOGO_URL,
                                            vibrate: [200, 100, 200]
                                        };
                                        
                                        if ('serviceWorker' in navigator) {
                                            navigator.serviceWorker.ready.then(registration => {
                                                registration.showNotification("BSD Digital Portal", options);
                                            });
                                        } else {
                                            new Notification("BSD Digital Portal", options);
                                        }
                                    }
                                }
                            }}
                            className="px-6 py-3 bg-blue-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 dark:shadow-none flex items-center gap-2"
                        >
                            <i className="fas fa-link"></i>
                            Link Device to Firebase Push
                        </button>
                        <div className="mt-4 flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                            <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Cloud Sync Status: Active</span>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-dark-card p-10 rounded-[3rem] border border-gray-100 dark:border-gray-800 shadow-xl">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl bg-orange-50 dark:bg-gray-800 flex items-center justify-center text-orange-500">
                                <i className="fas fa-key"></i>
                            </div>
                            <h3 className="text-xl font-black dark:text-white">Security & Access</h3>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium leading-relaxed mb-6">Managed by Google Authentication. To change your password, please use the reset link in the login portal or manage via your Google Account.</p>
                        <button className="px-6 py-3 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-gray-200 transition-all">
                            Manage Authentication
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfileManagement;
